import { ResultOf, readFragment } from "gql.tada";
import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import cx from "classnames";
import { AudienceCulturalInfluenceSectionById } from "@queries/entries/AudienceCulturalInfluenceById";
import { MediaGallerySection } from "./MediaGallerySection";
import NumberAnimation from "@components/shared/NumberAnimation";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import { RichText } from "@components/shared/core/RichText";
import { LinkFragment } from "@queries/fragments/LinkFragment";

export const AudienceCulturalInfluenceSection = ({
  entry: { audienceCulturalInfluenceSection: entry },
}: {
  entry: ResultOf<typeof AudienceCulturalInfluenceSectionById>;
}) => {
  const sourceInfoOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <p className="text-brand-cream/50 text-label text-center">{children}</p>
      ),
    },
  } satisfies Options;

  if (!entry || !entry?.mediaGallery) return null;

  const ctaLink = readFragment(LinkFragment, entry.ctaLink);

  return (
    <div className="dsk:min-h-screen py-10 flex flex-col justify-start gap-6 relative">
      <div className="text-body-small text-center">{entry.title}</div>
      {/* Number animation */}
      {!!entry.numberTitle && (
        <div
          className="absolute z-10 top-24 dsk:top-32 left-1/2"
          style={{
            transform: "translateX(-50%)",
          }}
        >
          <NumberAnimation
            value={entry.numberTitle}
            isPercentage={entry.isPercentage ?? false}
          />
        </div>
      )}
      {/* Media container */}
      <div
        className={cx(
          "max-h-[300px] dsk:max-h-[600px] pt-5",
          entry.numberTitle ? "dsk:mt-24" : ""
        )}
      >
        <MediaGallerySection entry={entry.mediaGallery} />
        {/* description field removed in schema migration */}
      </div>
      {!!entry.sourceRichText && (
        <div className="-mt-10 pb-10 dsk:-mt-24 dsk:pb-24">
          <RichText content={entry.sourceRichText} options={sourceInfoOptions} />
        </div>
      )}
      {!!ctaLink && (
        <div className="mx-auto flex dsk:flex-grow items-center">
          <Button variant="light-on-blur" size="large">
            <Link link={ctaLink}>{ctaLink.text}</Link>
          </Button>
        </div>
      )}
    </div>
  );
};
